/**
 * The map's fullscreen toggle.
 *
 * Two mechanisms, and the CSS one is the real one. `.crm-mapfull` is a fixed,
 * inset-0 layer, so the map fills the window whether or not the browser grants
 * real fullscreen; `requestFullscreen` is asked for on top of that because it
 * also takes the browser's own chrome away, and it is allowed to fail silently
 * because nothing depends on it. A toggle that did nothing when a browser
 * refused the request would be the worse trade.
 *
 * ⚠ The map's host element must NOT move in the DOM when this flips. Google
 * attaches its whole render tree to that div, and React recreating it would
 * leave the map instance drawing into a node no longer in the document. So this
 * hook returns a flag that only ever changes CLASSES, never structure, and the
 * layout difference lives in `index.css` under `.crm-mapfull`.
 */
import { useCallback, useEffect, useState } from "react";

export interface MapFullscreen {
  full: boolean;
  toggle: () => void;
}

/**
 * @param host The element to ask the browser to make fullscreen, and the one
 *   whose size change the caller has to tell the map about.
 * @param onResize Called after the layout has flipped, so the caller can put the
 *   map back on the centre it was showing.
 */
export function useMapFullscreen(
  host: React.RefObject<HTMLElement | null>,
  onResize: () => void,
): MapFullscreen {
  const [full, setFull] = useState(false);

  const toggle = useCallback(() => {
    setFull((wasFull) => {
      const next = !wasFull;
      const el = host.current;
      // Both calls can reject - a browser can refuse the request outright, and
      // exitFullscreen throws when nothing is fullscreen (which is the normal
      // case when the CSS layer was doing the work on its own).
      if (next) void el?.requestFullscreen?.().catch(() => {});
      else if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
      return next;
    });
  }, [host]);

  // Escape leaves. The browser fires its own Escape handling for real
  // fullscreen, but the CSS layer has none of its own, and a full-window map
  // with no visible way out is a trap.
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  // Leaving fullscreen through the browser's own affordance (F11, the Escape
  // the browser itself consumed, the tab losing it) has to bring the CSS layer
  // back with it, or the page stays covered by a layer nothing can now close.
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) setFull(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // The map keeps its top-left corner across a resize, not its centre, so a
  // toggle would otherwise slide the country sideways. Deferred by a frame
  // because the class that changes the size has not been painted yet.
  useEffect(() => {
    const id = requestAnimationFrame(onResize);
    return () => cancelAnimationFrame(id);
  }, [full, onResize]);

  return { full, toggle };
}
